import { useCallback, useEffect, useState } from 'react'
import type { Room, RpcInvocationData } from 'livekit-client'

// RPC method names (must match agent)
const RPC_SHOW_PRESENT_TEXT = 'showPresentText'
const RPC_SHOW_SHADOW_PRACTICE = 'showShadowPracticeText'
const RPC_CORRECT_MISTAKE = 'correctStudentMistake'
const RPC_DONE_ACCOMPLISHMENT = 'doneAccomplishment'
const RPC_CLEAR_DISPLAY = 'clearDisplay'

// Auto-dismiss timeouts (ms)
const PRESENT_TEXT_TIMEOUT = 30000 // 30 seconds
const SHADOW_PRACTICE_TIMEOUT = 30000 // 30 seconds
const CORRECTION_TIMEOUT = 15000 // 15 seconds
const ACCOMPLISHMENT_TIMEOUT = 10000 // 10 seconds

// Visual tool types
export type VisualToolType = 'present' | 'shadow' | 'correction' | 'accomplishment' | null

export interface PresentTextData {
  text: string
  category: 'vocabulary' | 'grammar' | 'phrase' | 'example'
  translation?: string
  example?: string
}

export interface ShadowPracticeData {
  sentence: string
  translation?: string
}

export interface CorrectionData {
  incorrect: string
  correct: string
  explanation?: string
}

export interface AccomplishmentData {
  message: string
}

export interface VisualToolsState {
  activeType: VisualToolType
  presentText: PresentTextData | null
  shadowPractice: ShadowPracticeData | null
  correction: CorrectionData | null
  accomplishment: AccomplishmentData | null
}

const initialState: VisualToolsState = {
  activeType: null,
  presentText: null,
  shadowPractice: null,
  correction: null,
  accomplishment: null,
}

interface UseVisualToolsReturn {
  state: VisualToolsState
  clearDisplay: () => void
}

export function useVisualTools(room: Room | null): UseVisualToolsReturn {
  const [state, setState] = useState<VisualToolsState>(initialState)
  const [dismissTimeout, setDismissTimeout] = useState<ReturnType<typeof setTimeout> | null>(null)

  // Clear any pending timeout
  const clearTimeout_ = useCallback(() => {
    if (dismissTimeout) {
      clearTimeout(dismissTimeout)
      setDismissTimeout(null)
    }
  }, [dismissTimeout])

  // Set auto-dismiss timeout
  const setAutoDismiss = useCallback((timeout: number) => {
    clearTimeout_()
    const id = setTimeout(() => {
      setState(initialState)
    }, timeout)
    setDismissTimeout(id)
  }, [clearTimeout_])

  // Clear all displays
  const clearDisplay = useCallback(() => {
    clearTimeout_()
    setState(initialState)
  }, [clearTimeout_])

  // Register RPC handlers
  useEffect(() => {
    if (!room) return

    // Handler for show_present_text
    const handlePresentText = async (data: RpcInvocationData): Promise<string> => {
      try {
        const payload = JSON.parse(data.payload) as PresentTextData
        setState({
          activeType: 'present',
          presentText: payload,
          shadowPractice: null,
          correction: null,
          accomplishment: null,
        })
        setAutoDismiss(PRESENT_TEXT_TIMEOUT)
        return JSON.stringify({ success: true })
      } catch (e) {
        console.error('Failed to parse present text payload:', e)
        return JSON.stringify({ success: false, error: 'Invalid payload' })
      }
    }

    // Handler for show_shadow_practice_text
    const handleShadowPractice = async (data: RpcInvocationData): Promise<string> => {
      try {
        const payload = JSON.parse(data.payload) as ShadowPracticeData
        setState({
          activeType: 'shadow',
          presentText: null,
          shadowPractice: payload,
          correction: null,
          accomplishment: null,
        })
        setAutoDismiss(SHADOW_PRACTICE_TIMEOUT)
        return JSON.stringify({ success: true })
      } catch (e) {
        console.error('Failed to parse shadow practice payload:', e)
        return JSON.stringify({ success: false, error: 'Invalid payload' })
      }
    }

    // Handler for correct_student_mistake
    const handleCorrection = async (data: RpcInvocationData): Promise<string> => {
      try {
        const payload = JSON.parse(data.payload) as CorrectionData
        setState({
          activeType: 'correction',
          presentText: null,
          shadowPractice: null,
          correction: payload,
          accomplishment: null,
        })
        setAutoDismiss(CORRECTION_TIMEOUT)
        return JSON.stringify({ success: true })
      } catch (e) {
        console.error('Failed to parse correction payload:', e)
        return JSON.stringify({ success: false, error: 'Invalid payload' })
      }
    }

    // Handler for done_accomplishment
    const handleAccomplishment = async (data: RpcInvocationData): Promise<string> => {
      try {
        const payload = JSON.parse(data.payload) as AccomplishmentData
        setState({
          activeType: 'accomplishment',
          presentText: null,
          shadowPractice: null,
          correction: null,
          accomplishment: payload,
        })
        setAutoDismiss(ACCOMPLISHMENT_TIMEOUT)
        return JSON.stringify({ success: true })
      } catch (e) {
        console.error('Failed to parse accomplishment payload:', e)
        return JSON.stringify({ success: false, error: 'Invalid payload' })
      }
    }

    // Handler for clear_display
    const handleClearDisplay = async (): Promise<string> => {
      clearDisplay()
      return JSON.stringify({ success: true })
    }

    // Register all RPC methods
    room.registerRpcMethod(RPC_SHOW_PRESENT_TEXT, handlePresentText)
    room.registerRpcMethod(RPC_SHOW_SHADOW_PRACTICE, handleShadowPractice)
    room.registerRpcMethod(RPC_CORRECT_MISTAKE, handleCorrection)
    room.registerRpcMethod(RPC_DONE_ACCOMPLISHMENT, handleAccomplishment)
    room.registerRpcMethod(RPC_CLEAR_DISPLAY, handleClearDisplay)

    console.log('Visual tools RPC handlers registered')

    // Cleanup: unregister RPC methods
    return () => {
      try {
        room.unregisterRpcMethod(RPC_SHOW_PRESENT_TEXT)
        room.unregisterRpcMethod(RPC_SHOW_SHADOW_PRACTICE)
        room.unregisterRpcMethod(RPC_CORRECT_MISTAKE)
        room.unregisterRpcMethod(RPC_DONE_ACCOMPLISHMENT)
        room.unregisterRpcMethod(RPC_CLEAR_DISPLAY)
        console.log('Visual tools RPC handlers unregistered')
      } catch (e) {
        // Room may already be disconnected
        console.warn('Failed to unregister RPC methods:', e)
      }
    }
  }, [room, setAutoDismiss, clearDisplay])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dismissTimeout) {
        clearTimeout(dismissTimeout)
      }
    }
  }, [dismissTimeout])

  return {
    state,
    clearDisplay,
  }
}
